import { ChevronRight } from '@mui/icons-material';
import {
    Button,
    IconButton,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { TableTemplateCell } from '/@/components/bayer/table/template-cell';
import ChipLabel from '/@/components/chip-label';
import Icon from '/@/components/icon';
import settings from '/@/core/settings';
import { useProcessRecommendation } from '/@/hooks/my-recommendations/useFilterMyRecommendations';
import { _ } from '/@/locale';
import { useGetRecommendationsFromAPI } from '/@/store/private-data/recommendations/apiProviders';
import type { Recommendation } from '/@/types/recommendation';
import { sliceString } from '/@/utils';
import { reverse } from '/@/utils/router';
import { useWhatIsRecommedation } from '../../../components/recommendations-list';
import CreateRecommendationModal from '../../../rv/components/create-recommendation-modal';

const RecommendationRow = ({ recommendation }: { recommendation: Recommendation }) => {
    const processRecommendation = useProcessRecommendation();
    const processedRecommendation = processRecommendation(recommendation);
    const whatIsRecommedation = useWhatIsRecommedation(recommendation);

    return (
        <TableRow>
            <TableCell>
                <ChipLabel
                    label={processedRecommendation.iconLabel}
                    type={processedRecommendation.type}
                    icon={<Icon type={processedRecommendation.icon} size='xs' theme={settings.app.theme} />}
                />
            </TableCell>
            <TableCell>
                <TableTemplateCell title={sliceString(processedRecommendation.fieldName, 15)} titleSize='body2' />
            </TableCell>
            <TableCell>
                <TableTemplateCell
                    title={whatIsRecommedation.title}
                    subtitle={whatIsRecommedation.subtitle}
                    titleSize='body2'
                />
            </TableCell>
            <TableCell
                sx={{
                    p: '0 !important',
                }}
            >
                <IconButton
                    color='secondary'
                    onClick={() => processedRecommendation.onClick(processedRecommendation)}
                    size='small'
                >
                    <ChevronRight />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

const RecommendationSection = ({ userNodeId }: { userNodeId?: number }) => {
    const navigate = useNavigate();
    const recommendationsListQuery = useGetRecommendationsFromAPI({
        page: 1,
        pageSize: 5,
        filters: {},
        ordering: '-created',
        node: userNodeId,
    });

    const [newRecommendationModalOpen, setNewRecommendationModalOpen] = useState(false);

    return (
        <Stack spacing={3}>
            <Stack spacing={1}>
                <Typography color='primary' variant='h5'>
                    {_('Recomendaciones')}
                </Typography>
                <Typography color='primary.mui.inactive' variant='body2'>
                    {_('Últimas recomendaciones generadas para tus lotes.')}
                </Typography>
            </Stack>
            <Stack spacing={2}>
                {userNodeId ? (
                    <Stack
                        sx={{
                            background: 'white',
                            border: (t) => `1px solid ${t.palette.divider}`,
                        }}
                    >
                        <Stack p={2} overflow='auto'>
                            <Table
                                sx={{
                                    '& .MuiTableCell-root': {
                                        px: 1,
                                        py: 1,
                                    },
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <Typography variant='subtitle2' color='primary' fontWeight='bold'>
                                                {_('Tipo')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant='subtitle2' color='primary' fontWeight='bold'>
                                                {_('Lote')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant='subtitle2' color='primary' fontWeight='bold'>
                                                {_('Recomendación')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody
                                    sx={{
                                        '& .MuiTableCell-root': {
                                            'word-break': 'break-word',
                                        },
                                    }}
                                >
                                    {recommendationsListQuery.data?.results?.data.map((recommendation) => (
                                        <RecommendationRow key={recommendation.id} recommendation={recommendation} />
                                    ))}
                                </TableBody>
                            </Table>
                        </Stack>
                        <Stack spacing={2} direction='row' px={3} py={2}>
                            <Button
                                variant='contained'
                                color='primary'
                                onClick={() => setNewRecommendationModalOpen(true)}
                                data-testid='generate-recommendation-button'
                            >
                                {_('Generar recomendación')}
                            </Button>
                            <Button
                                variant='text'
                                color='primary'
                                onClick={() => navigate(reverse('recommendations.List'))}
                            >
                                {_('Ver todas')}
                            </Button>
                        </Stack>
                    </Stack>
                ) : (
                    <Skeleton variant='rectangular' height='500px' width='100%' />
                )}
            </Stack>

            <CreateRecommendationModal
                open={newRecommendationModalOpen}
                setOpen={setNewRecommendationModalOpen}
                userNodeId={userNodeId}
                entrySource='recommendation-list'
            />
        </Stack>
    );
};

export default RecommendationSection;
